import { useCallback, useState } from "react";
import axios from "axios";
import {
  cadastrarAssinaturaService,
  GerarCobrancaAssinaturaPayload,
  GerarCobrancaAssinaturaResponse,
} from "../../../services/cadastrarAssinatura";

type AxiosErrorData = {
  message?: string;
  error?: string;
};

type CriarAssinaturaPayload = Omit<
  GerarCobrancaAssinaturaPayload,
  "pessoaId"
> & {
  nome: string;
  celular: string;
  cpf: string;
};

type CadastroAssinaturaResultado = {
  data: GerarCobrancaAssinaturaResponse;
  variant: "success" | "warning";
  message: string;
};

const getCadastroAssinaturaFeedback = (
  data: GerarCobrancaAssinaturaResponse,
  enviarWhatsapp?: boolean
): CadastroAssinaturaResultado => {
  if (!enviarWhatsapp) {
    return {
      data,
      variant: "success",
      message: "Assinatura criada com sucesso!",
    };
  }

  if (data.whatsapp?.enviado) {
    return {
      data,
      variant: "success",
      message: "Assinatura criada e mensagem enviada com sucesso!",
    };
  }

  if (data.whatsapp?.processando) {
    return {
      data,
      variant: "success",
      message:
        data.mensagem ||
        "Assinatura criada e envio do WhatsApp em processamento.",
    };
  }

  if (data.whatsapp?.tentado) {
    return {
      data,
      variant: "warning",
      message:
        "Assinatura criada com sucesso, mas nao foi possivel enviar a mensagem pelo WhatsApp.",
    };
  }

  return {
    data,
    variant: "warning",
    message:
      "Assinatura criada com sucesso, mas nao foi possivel confirmar o envio da mensagem.",
  };
};

export const useAdicionarAssinatura = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetError = useCallback(() => setError(null), []);

  const cadastrar = async (
    assinaturaData: CriarAssinaturaPayload
  ): Promise<CadastroAssinaturaResultado | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const cobrancaPayload: GerarCobrancaAssinaturaPayload = {
        nome: assinaturaData.nome.trim(),
        celular: assinaturaData.celular,
        cpf: assinaturaData.cpf,
        quantidadeCotas: assinaturaData.quantidadeCotas,
        formaPagamento: assinaturaData.formaPagamento,
        planoAssinatura: assinaturaData.planoAssinatura,
        enviarWhatsapp: assinaturaData.enviarWhatsapp,
      };

      const data = await cadastrarAssinaturaService(cobrancaPayload);

      if (data?.assinatura?.id) {
        return getCadastroAssinaturaFeedback(data, assinaturaData.enviarWhatsapp);
      }

      setError("Erro ao gerar cobranca da assinatura.");
      return null;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const responseData = err.response?.data as AxiosErrorData | undefined;
        const errorMessage =
          responseData?.message ||
          responseData?.error ||
          err.message ||
          "Ocorreu um erro ao gerar a cobranca.";

        setError(errorMessage);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Ocorreu um erro ao gerar a cobranca.");
      }

      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, error, cadastrar, resetError };
};
